// fallow-ignore-file
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSupplier } from '../queryKeys';

import { postSuppliersRelevance } from './postSuppliersRelevance';
import { type PostSuppliersRelevanceMutationOptions } from './types';

/**
 * Mutation key for the supplier relevance toggle.
 * Used with `useMutationState` to track pending toggles per supplier.
 */
export const MUTATION_KEY_POST_SUPPLIERS_RELEVANCE = [
  'supplier',
  'relevance',
] as const;

/**
 * React Query based custom hook for marking a supplier as relevant or irrelevant.
 *
 * Concurrent calls are allowed: each invocation targets a distinct supplier
 * (different `dataExchangeId`), so the requests do not conflict in payload and
 * can run in parallel.
 *
 * To avoid an invalidation storm during rapid bursts of clicks, `onSettled`
 * only invalidates the supplier overview and network queries when this
 * mutation is the last in-flight `['supplier', ...]` mutation. This coalesces
 * a burst (across both relevance toggles and network adds) into a single
 * refetch on completion. `onSettled` is used rather than `onSuccess` so that
 * earlier successes still get reconciled even when the burst ends with a
 * failed mutation.
 *
 * Consumers can override `mutationKey` or any callback via options.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Supplier-overview/operation/post_suppliers_relevance_suppliers_views_relevance_post
 */
export const useMutationPostSuppliersRelevance = (
  options?: PostSuppliersRelevanceMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dataExchangeId, isRelevant }) =>
      postSuppliersRelevance({ dataExchangeId, isRelevant }),
    mutationKey: MUTATION_KEY_POST_SUPPLIERS_RELEVANCE,
    onSettled() {
      // Only invalidate when this is the last in-flight supplier mutation so a
      // burst of clicks coalesces into a single refetch. The current mutation
      // is still counted as `pending` here, hence `=== 1`.
      if (queryClient.isMutating({ mutationKey: ['supplier'] }) === 1) {
        void queryClient.invalidateQueries({
          queryKey: queryKeysSupplier.getNetwork(),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeysSupplier.getOverview(),
        });
      }
    },
    throwOnError: false,
    ...options,
  });
};
